import { useQuery } from '@tanstack/react-query';
import { STATUS_TONE } from '@/lib/status';
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { INCIDENT_STATUSES, slaState, type ChecklistRunStatus, type Incident, type IncidentSeverity, type IncidentStatus } from '@tradeoutlet/shared';
import { dt } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { Badge } from '@/components/ui';

export const opsKeys = (ws: string, ...rest: unknown[]) => ['ops', ws, ...rest] as const;

export function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const SEV: Record<IncidentSeverity, string> = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  medium: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
  critical: 'bg-rose-600 text-white',
};
export function SeverityBadge({ s }: { s: IncidentSeverity }) { return <Badge className={SEV[s]}>{s[0].toUpperCase() + s.slice(1)}</Badge>; }

export function IncidentStatusBadge({ s }: { s: IncidentStatus }) {
  const open = INCIDENT_STATUSES.find(x => x.key === s)?.open;
  return <Badge className={open ? (s === 'new' ? 'bg-accent text-on-accent' : STATUS_TONE.submitted) : STATUS_TONE.approved}>
    {INCIDENT_STATUSES.find(x => x.key === s)?.label ?? s}</Badge>;
}

export function SlaChip({ t }: { t: Pick<Incident, 'status' | 'created_at' | 'resolve_due_at' | 'resolved_at'> }) {
  const s = slaState(t);
  if (s === 'none') return null;
  const due = t.resolve_due_at ? dt(t.resolve_due_at) : null;
  const label = s === 'met' ? 'SLA met' : s === 'breached' ? 'SLA breached' : `Due ${due!.toFormat(due!.hasSame(dt(new Date().toISOString()), 'day') ? 'HH:mm' : 'ccc d LLL, HH:mm')}`;
  const cls = s === 'breached' ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200' : s === 'due_soon' ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
    : s === 'met' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return <Badge className={cls}>{s === 'breached' ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{label}</Badge>;
}

export function RunStatusBadge({ s, late, passed }: { s: ChecklistRunStatus; late?: boolean; passed?: boolean | null }) {
  if (s === 'completed') {
    return <span className="inline-flex gap-1">
      <Badge className={passed === false ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'}>
        {passed === false ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{passed === false ? 'Done - issues' : 'Done'}</Badge>
      {late && <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200">Late</Badge>}
    </span>;
  }
  if (s === 'scheduled') return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">Scheduled</Badge>;
  if (s === 'missed') return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200"><AlertTriangle className="h-3 w-3" />Missed</Badge>;
  return <Badge className={s === 'in_progress' ? STATUS_TONE.submitted : 'bg-accent text-on-accent'}>{s === 'in_progress' ? 'In progress' : 'To do'}</Badge>;
}

/** Signed URL for a feature-owned upload, fetched lazily and cached for 4 minutes. */
export function useFileUrl(key: readonly unknown[], fetcher: () => Promise<{ url: string }>, enabled = true) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['ops-file', ws, ...key], queryFn: fetcher, enabled: Boolean(ws && enabled), staleTime: 240_000 });
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const fmtDays = (d: number[]) => d.length === 7 ? 'Every day' : d.join(',') === '1,2,3,4,5' ? 'Weekdays' : d.map(n => WEEKDAYS[n - 1]).join(', ');
